import type { Rng } from "./rng";
import type { Direction, ProgressRecord, ProgressState, VocabEntry } from "./types";

export const SESSION_COMPLETE = "complete" as const;

/**
 * Resultat eines pickNext-Calls. Vor Issue #95 war es nur ein english-String;
 * jetzt zusätzlich die Direction, damit der aufrufende Store den Prompt
 * korrekt rendern und beim recordAnswer in die richtige Direction-Row
 * schreiben kann (Race-Schutz gegen gleichzeitigen Mode-Switch).
 */
export type ScheduleResult =
  | { english: string; direction: Direction }
  | typeof SESSION_COMPLETE;

/**
 * Scope für den Scheduler — leitet sich aus settings.learningDirection ab:
 *  - `en_de` oder `de_en`: einzige Direction
 *  - `mixed`: pro Frage zufällig 50/50
 */
export type SchedulerScope = Direction | "mixed";

// Per ADR-004: drill fresh vocab broadly before in_progress confirmations
// re-appear. Wrong words still take priority for fast correction.
const BUCKET_ORDER: ProgressState[] = ["wrong", "fresh", "in_progress"];

// Minimum number of intervening questions between repeats of a just-correct
// word. Set high enough that "Bestätigung" (the 2nd correct on a fresh word)
// is never adjacent to the 1st correct. SM-2 light still grows the gap for
// long correct streaks but never below MIN_GAP_CORRECT.
export const MIN_GAP_CORRECT = 8;

// Random window for re-appearance of wrong-answered words. Wide enough to
// avoid feeling like a drill hammer, narrow enough to still drive correction.
export const WRONG_GAP_MIN = 3;
export const WRONG_GAP_MAX = 6;

export function pickNext(
  progress: readonly ProgressRecord[],
  questionsAnswered: number,
  rng: Rng,
  scope: SchedulerScope = "en_de",
  markedOnly?: ReadonlySet<string>,
): ScheduleResult {
  // Issue #123: wenn markedOnly gesetzt, kommt nur ein vorgefilterter Pool an
  // pickForDirection vorbei. Im 'marked'-Modus ist scope weiterhin die
  // gewählte Direction — der User hat sowohl Direction als auch Mode aktiv,
  // also respektieren wir beides.
  const pool = markedOnly
    ? progress.filter((p) => markedOnly.has(p.english))
    : progress;

  // Im Mixed-Modus wählt der Scheduler pro Frage zufällig eine Direction. Wenn
  // die Erst-Wahl-Direction nichts mehr offen hat, fallen wir auf die andere
  // zurück. Erst wenn beide leer → SESSION_COMPLETE.
  if (scope === "mixed") {
    const firstChoice: Direction = rng.intBetween(0, 2) === 0 ? "en_de" : "de_en";
    const fallback: Direction = firstChoice === "en_de" ? "de_en" : "en_de";
    const result = pickForDirection(pool, questionsAnswered, rng, firstChoice);
    if (result !== SESSION_COMPLETE) return result;
    return pickForDirection(pool, questionsAnswered, rng, fallback);
  }

  return pickForDirection(pool, questionsAnswered, rng, scope);
}

function pickForDirection(
  progress: readonly ProgressRecord[],
  questionsAnswered: number,
  rng: Rng,
  direction: Direction,
): ScheduleResult {
  const inDirection = progress.filter((p) => p.direction === direction);
  const open = inDirection.filter((p) => p.state !== "mastered");
  if (open.length === 0) return SESSION_COMPLETE;

  const due = open.filter((p) => p.earliestQuestionIndex <= questionsAnswered);
  const pool = due.length > 0 ? due : open;

  for (const bucket of BUCKET_ORDER) {
    const bucketItems = pool.filter((p) => p.state === bucket);
    if (bucketItems.length > 0) {
      const picked = rng.pick(bucketItems);
      return { english: picked.english, direction };
    }
  }

  const picked = rng.pick(pool);
  return { english: picked.english, direction };
}

export function nextEarliestIndexAfterCorrect(
  questionsAnswered: number,
  consecutiveCorrectAfter: number,
): number {
  const safe = Math.max(1, consecutiveCorrectAfter);
  const sm2Gap = Math.floor(Math.pow(1.5, safe));
  const gap = Math.max(MIN_GAP_CORRECT, sm2Gap);
  return questionsAnswered + gap;
}

export function nextEarliestIndexAfterWrong(questionsAnswered: number, rng: Rng): number {
  return questionsAnswered + rng.intBetween(WRONG_GAP_MIN, WRONG_GAP_MAX);
}

export function lookupVocab(vocab: readonly VocabEntry[], english: string): VocabEntry {
  const found = vocab.find((v) => v.english === english);
  if (!found) throw new Error(`Vocab missing for english=${english}`);
  return found;
}
